import { useCallback, useState, type ReactNode } from "react";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@project/admin/components/searchable-select";
import {
  EMPTY_LOCATION_LABELS,
  locationSeedOption,
  resolveLocationLabels,
  searchAreaOptions,
  searchCityOptions,
  searchDistrictOptions,
  type LocationLabels,
} from "@project/components/location-cascading-selects";
import {
  FileText,
  Hash,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Receipt,
  Search,
  User,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import {
  donationAddService,
  donationDonorService,
} from "@project/services/admin/donation-donor.service";
import {
  PAYMENT_MODE_OPTIONS,
  PURPOSE_OF_DONATION_OPTIONS,
  formatDonationAmount,
  type AddDonationPayload,
} from "@project/types/donation-donor";

type DonationTargetMode = "campaign" | "other";

type AddDonationFormState = {
  donationTarget: DonationTargetMode;
  scheme_id: string;
  purpose_of_donation: string;
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  message: string;
  mode: string;
  payment_mode: string;
  district: string;
  city: string;
  area: string;
  pincode: string;
  by_cheque_no: string;
};

const addDonationInitialState: AddDonationFormState = {
  donationTarget: "campaign",
  scheme_id: "",
  purpose_of_donation: "",
  name: "",
  email: "",
  phone: "",
  donation_type: "",
  amount: "",
  message: "",
  mode: "offline",
  payment_mode: "",
  district: "",
  city: "",
  area: "",
  pincode: "",
  by_cheque_no: "",
};

type DonationTypeOption = { value: string; label: string };
type SchemeOption = { value: string; label: string };

type AddDonationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donationTypes: DonationTypeOption[];
  schemes: SchemeOption[];
  onSuccess: () => void;
};

export function AddDonationDialog({
  open,
  onOpenChange,
  donationTypes,
  schemes,
  onSuccess,
}: AddDonationDialogProps) {
  const [addForm, setAddForm] = useState<AddDonationFormState>(
    addDonationInitialState
  );
  const [phoneSearch, setPhoneSearch] = useState("");
  const [phoneSearching, setPhoneSearching] = useState(false);
  const [phoneLookedUp, setPhoneLookedUp] = useState(false);
  const [foundDonorId, setFoundDonorId] = useState<string | number | null>(
    null
  );
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [locationLabels, setLocationLabels] = useState<LocationLabels>(
    EMPTY_LOCATION_LABELS
  );

  const resetForm = useCallback(() => {
    setPhoneSearch("");
    setPhoneLookedUp(false);
    setAddForm(addDonationInitialState);
    setFoundDonorId(null);
    setLocationLabels(EMPTY_LOCATION_LABELS);
  }, []);

  const searchDistricts = useCallback(
    (query: string) => searchDistrictOptions(query),
    []
  );

  const searchCities = useCallback(
    (query: string) => searchCityOptions(addForm.district, query),
    [addForm.district]
  );

  const searchAreas = useCallback(
    (query: string) => searchAreaOptions(addForm.city, query),
    [addForm.city]
  );

  const handleDistrictChange = (
    district: string,
    option?: SearchableSelectOption
  ) => {
    setAddForm(prev => ({
      ...prev,
      district,
      city: "",
      area: "",
    }));
    setLocationLabels({
      districtName: option?.label ?? "",
      cityName: "",
      areaName: "",
    });
  };

  const handleCityChange = (city: string, option?: SearchableSelectOption) => {
    setAddForm(prev => ({
      ...prev,
      city,
      area: "",
    }));
    setLocationLabels(prev => ({
      ...prev,
      cityName: option?.label ?? "",
      areaName: "",
    }));
  };

  const handleAreaChange = (area: string, option?: SearchableSelectOption) => {
    setAddForm(prev => ({ ...prev, area }));
    setLocationLabels(prev => ({
      ...prev,
      areaName: option?.label ?? "",
    }));
  };

  const handlePhoneSearch = useCallback(async () => {
    const phone = phoneSearch.trim();
    if (!phone || phone.length < 10) {
      projectToast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setPhoneSearching(true);
    try {
      const donor = await donationDonorService.searchByPhone(phone);
      if (donor) {
        const district = String(donor.district_id ?? donor.district ?? "");
        const city = String(donor.city_id ?? donor.city ?? "");
        const area = String(donor.area_id ?? donor.area ?? "");
        setAddForm(prev => ({
          ...prev,
          name: donor.name ?? "",
          email: donor.email ?? "",
          phone: donor.phone ?? phone,
          donation_type: donor.donation_type ?? "",
          amount: donor.amount ? String(donor.amount) : "",
          message: donor.message ?? "",
          payment_mode: donor.payment_mode?.toLowerCase() ?? "",
          district,
          city,
          area,
          pincode: donor.pincode ?? "",
          by_cheque_no: donor.by_cheque_no ?? "",
        }));
        const seededLabels: LocationLabels = {
          districtName: donor.district_name ?? "",
          cityName: donor.city_name ?? "",
          areaName: donor.area_name ?? "",
        };
        setLocationLabels(seededLabels);
        void resolveLocationLabels(
          { districtId: district, cityId: city, areaId: area },
          seededLabels
        ).then(setLocationLabels);
        setFoundDonorId(donor.id);
        projectToast.success("Donor found! Form has been pre-filled.");
      } else {
        setAddForm({ ...addDonationInitialState, phone });
        setLocationLabels(EMPTY_LOCATION_LABELS);
        setFoundDonorId(null);
        projectToast.info("No donor found. Please fill the details manually.");
      }
      setPhoneLookedUp(true);
    } catch {
      projectToast.error("Error searching for donor");
    } finally {
      setPhoneSearching(false);
    }
  }, [phoneSearch]);

  const handleSubmit = useCallback(async () => {
    if (addForm.donationTarget === "campaign" && !addForm.scheme_id) {
      return projectToast.error("Campaign is required");
    }
    if (
      addForm.donationTarget === "other" &&
      !addForm.purpose_of_donation.trim()
    ) {
      return projectToast.error("Purpose of donation is required");
    }
    if (!addForm.name.trim()) {
      return projectToast.error("Name is required");
    }
    if (!addForm.email.trim()) {
      return projectToast.error("Email is required");
    }
    if (!addForm.phone.trim()) {
      return projectToast.error("Phone is required");
    }
    if (!addForm.donation_type) {
      return projectToast.error("Donation type is required");
    }
    if (!addForm.amount || parseFloat(addForm.amount) <= 0) {
      return projectToast.error("Valid donation amount is required");
    }
    if (!addForm.payment_mode) {
      return projectToast.error("Payment mode is required");
    }
    if (!addForm.district) {
      return projectToast.error("District is required");
    }
    if (!addForm.city) {
      return projectToast.error("City is required");
    }
    if (!addForm.area) {
      return projectToast.error("Area is required");
    }
    if (!addForm.pincode.trim() || !/^[0-9]{6}$/.test(addForm.pincode)) {
      return projectToast.error("Valid 6-digit pincode is required");
    }

    const payload: AddDonationPayload = {
      name: addForm.name.trim(),
      email: addForm.email.trim(),
      phone: addForm.phone.trim(),
      donation_type: addForm.donation_type,
      amount: formatDonationAmount(addForm.amount),
      mode: "offline",
      payment_mode: addForm.payment_mode.toLowerCase(),
      district: Number(addForm.district),
      city: Number(addForm.city),
      area: Number(addForm.area),
      pincode: addForm.pincode.trim(),
    };

    if (addForm.donationTarget === "campaign") {
      payload.scheme_id = Number(addForm.scheme_id);
    } else {
      payload.purpose_of_donation = addForm.purpose_of_donation.trim();
    }

    const chequeNo = addForm.by_cheque_no.trim();
    const message = addForm.message.trim();
    if (chequeNo) {
      payload.by_cheque_no = chequeNo;
    }
    if (message) {
      payload.message = message;
    }

    setAddSubmitting(true);
    try {
      await donationAddService.addDonation(payload);
      projectToast.success("Donation added successfully!");
      onOpenChange(false);
      resetForm();
      onSuccess();
    } catch (e) {
      projectToast.error(getApiErrorMessage(e, "Failed to add donation"));
    } finally {
      setAddSubmitting(false);
    }
  }, [addForm, onOpenChange, onSuccess, resetForm]);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next);
        if (!next) {
          resetForm();
        }
      }}
    >
      <DialogContent className="flex max-h-[95vh] max-w-2xl flex-col overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:right-6 [&>button]:top-6 [&>button]:text-white/80 [&>button]:hover:text-white">
        <div className="shrink-0 bg-primary px-6 py-6 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/20 p-2">
              <Plus className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold tracking-tight">
                Manual Donation Entry
              </DialogTitle>
              <p className="text-primary-foreground/70 mt-0.5 text-xs font-medium uppercase tracking-wider">
                Record contribution from existing or new donor
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-8 overflow-y-auto bg-background px-6 py-8">
          <div>
            <Label htmlFor="add-phone-search" className="text-sm font-semibold">
              Mobile Number <span className="text-destructive">*</span>
            </Label>
            <div className="mt-2 flex gap-3">
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="add-phone-search"
                  className="h-11 pl-10"
                  value={phoneSearch}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setPhoneSearch(val);
                    if (phoneLookedUp) {
                      setPhoneLookedUp(false);
                      setAddForm(addDonationInitialState);
                      setFoundDonorId(null);
                      setLocationLabels(EMPTY_LOCATION_LABELS);
                    }
                  }}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                  disabled={phoneSearching}
                />
              </div>
              <Button
                onClick={() => void handlePhoneSearch()}
                disabled={phoneSearching || phoneSearch.length < 10}
                className="h-11 shrink-0 px-6"
              >
                {phoneSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2 font-semibold">Search Donor</span>
              </Button>
            </div>
            {!phoneLookedUp && !phoneSearching && (
              <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Hash className="h-3 w-3" />
                Search by phone to auto-fill existing donor details
              </p>
            )}
          </div>

          {phoneLookedUp && (
            <div className="animate-in fade-in slide-in-from-top-4 mt-6 space-y-6 border-t pt-6 duration-300">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Donor Details</h3>
                {foundDonorId && (
                  <Badge className="border-none bg-green-100 text-green-800 hover:bg-green-100">
                    Existing Donor
                  </Badge>
                )}
              </div>

              <div className="grid gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">
                    Donation For *
                  </Label>
                  <RadioGroup
                    value={addForm.donationTarget}
                    onValueChange={value =>
                      setAddForm(prev => ({
                        ...prev,
                        donationTarget: value as DonationTargetMode,
                        scheme_id: "",
                        purpose_of_donation: "",
                      }))
                    }
                    className="border-border/70 grid overflow-hidden rounded-xl border bg-card sm:grid-cols-2 sm:divide-x sm:divide-border"
                  >
                    <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
                      <RadioGroupItem value="campaign" />
                      Campaign
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
                      <RadioGroupItem value="other" />
                      Other
                    </label>
                  </RadioGroup>
                </div>

                {addForm.donationTarget === "campaign" ? (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Campaign *</Label>
                    <Select
                      value={addForm.scheme_id || undefined}
                      onValueChange={value =>
                        setAddForm(prev => ({ ...prev, scheme_id: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select campaign" />
                      </SelectTrigger>
                      <SelectContent>
                        {schemes.map(scheme => (
                          <SelectItem key={scheme.value} value={scheme.value}>
                            {scheme.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Purpose of Donation *
                    </Label>
                    <Select
                      value={addForm.purpose_of_donation || undefined}
                      onValueChange={value =>
                        setAddForm(prev => ({
                          ...prev,
                          purpose_of_donation: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        {PURPOSE_OF_DONATION_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <FormField
                  label="Full Name *"
                  icon={<User className="h-4 w-4" />}
                >
                  <Input
                    value={addForm.name}
                    onChange={e =>
                      setAddForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Donor name"
                    className="pl-10"
                  />
                </FormField>

                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField
                    label="Email Address *"
                    icon={<Mail className="h-4 w-4" />}
                  >
                    <Input
                      type="email"
                      value={addForm.email}
                      onChange={e =>
                        setAddForm(prev => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="donor@example.com"
                      className="pl-10"
                    />
                  </FormField>
                  <FormField
                    label="Contact Phone *"
                    icon={<Phone className="h-4 w-4" />}
                  >
                    <Input
                      value={addForm.phone}
                      onChange={e =>
                        setAddForm(prev => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder="Phone number"
                      maxLength={10}
                      className="pl-10"
                    />
                  </FormField>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Donation Type *
                    </Label>
                    <Select
                      value={addForm.donation_type || undefined}
                      onValueChange={value =>
                        setAddForm(prev => ({ ...prev, donation_type: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {donationTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Payment Amount (₹) *
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-medium text-muted-foreground">
                        ₹
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={addForm.amount}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                        className="pl-8"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Payment Mode *
                    </Label>
                    <Select
                      value={addForm.payment_mode || undefined}
                      onValueChange={value =>
                        setAddForm(prev => ({ ...prev, payment_mode: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_MODE_OPTIONS.map(mode => (
                          <SelectItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <FormField
                    label="Ref No. / Cheque No."
                    icon={<Receipt className="h-4 w-4" />}
                  >
                    <Input
                      value={addForm.by_cheque_no}
                      onChange={e =>
                        setAddForm(prev => ({
                          ...prev,
                          by_cheque_no: e.target.value,
                        }))
                      }
                      placeholder="Enter transaction ref or cheque number"
                      className="pl-10"
                    />
                  </FormField>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">
                    Additional Remarks
                  </Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Textarea
                      value={addForm.message}
                      onChange={e =>
                        setAddForm(prev => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder="Enter any additional donation details"
                      rows={3}
                      className="min-h-[80px] pl-10"
                    />
                  </div>
                </div>

                <div className="bg-muted/40 mt-4 space-y-5 rounded-xl border p-5">
                  <div className="flex items-center gap-2 text-primary">
                    <MapPin className="h-5 w-5" />
                    <h3 className="text-sm font-bold uppercase tracking-wide">
                      Address Information
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">
                          District *
                        </Label>
                        <SearchableSelect
                          value={addForm.district}
                          onValueChange={handleDistrictChange}
                          onSearch={searchDistricts}
                          seedOption={locationSeedOption(
                            addForm.district,
                            locationLabels.districtName
                          )}
                          placeholder="Select district"
                          searchPlaceholder="Search district..."
                          emptyText="No district found."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">
                          City *
                        </Label>
                        <SearchableSelect
                          value={addForm.city}
                          onValueChange={handleCityChange}
                          onSearch={searchCities}
                          seedOption={locationSeedOption(
                            addForm.city,
                            locationLabels.cityName
                          )}
                          placeholder={
                            addForm.district
                              ? "Select city"
                              : "Select district first"
                          }
                          searchPlaceholder="Search city..."
                          emptyText={
                            addForm.district
                              ? "No city found."
                              : "Select a district first."
                          }
                          disabled={!addForm.district}
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">
                          Area *
                        </Label>
                        <SearchableSelect
                          value={addForm.area}
                          onValueChange={handleAreaChange}
                          onSearch={searchAreas}
                          seedOption={locationSeedOption(
                            addForm.area,
                            locationLabels.areaName
                          )}
                          placeholder={
                            addForm.city ? "Select area" : "Select city first"
                          }
                          searchPlaceholder="Search area..."
                          emptyText={
                            addForm.city
                              ? "No area found."
                              : "Select a city first."
                          }
                          disabled={!addForm.city}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">
                          Pincode *
                        </Label>
                        <Input
                          value={addForm.pincode}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              pincode: e.target.value.replace(/[^0-9]/g, ""),
                            }))
                          }
                          placeholder="6-digit pincode"
                          maxLength={6}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {phoneLookedUp && (
          <DialogFooter className="gap-3 border-t bg-background px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={addSubmitting}
              onClick={() => void handleSubmit()}
            >
              {addSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save Donation"
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        {children}
      </div>
    </div>
  );
}
